import { useEffect, useState } from "react";

import { notFound } from "./NotFoundPage.jsx";
import { head } from "../components/common/PageHeader.jsx";
import {
  btn,
  field,
  select,
  area,
  errorSlot,
} from "../components/forms/FormControls.jsx";
import { detailRows } from "../components/common/DetailRows.jsx";
import { money, esc } from "../utils/format.js";
import { tableViews } from "../components/tables/RecordTable.jsx";
import { state } from "../state/workspaceStore.js";

const CUSTOMERS_API = "https://api.praachinorganics.com/api/Customers";

function getStatus(status) {
  const statuses = {
    1: "Active",
    2: "Inactive",
    3: "Blocked",
  };

  return statuses[status] || status || "";
}

function toDateInput(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
  }

  return date.toISOString().slice(0, 10);
}

export function customerEdit(
  cid,
  customerData,
  onSubmit,
  saving = false,
  error = "",
  success = "",
) {
  if (!customerData) return notFound();

  const customerId = customerData.customerProfileId;

  const registration = toDateInput(customerData.registrationDate);

  const status = String(customerData.status ?? "");

  return (
    <>
      {head(
        "CUSTOMERS",
        "Edit " + (customerData.customerName || "Customer"),
        "Edit the shared customer record.",
        btn("Cancel", `data-nav="customer" data-id="${customerId}"`, true),
      )}

      <form
        id={"customer-edit-form"}
        className={"card settings-simple"}
        onSubmit={onSubmit}
      >
        <h2>{"Customer record"}</h2>

        <div className={"form-grid"}>
          {field(
            "Customer ID",
            "customerId",
            customerData.customerIdentifier || customerId,
            "text",
            "readonly",
          )}

          {field(
            "Name *",
            "name",
            customerData.customerName || "",
            "text",
            'required maxlength="120"',
          )}

          {field(
            "Email *",
            "email",
            customerData.email || "",
            "email",
            "required",
          )}

          {field("Phone", "phone", customerData.phone || "", "tel")}

          {field("City / location", "city", customerData.city || "")}

          {field("State", "state", customerData.state || "")}
          {field("Postal Code", "postalCode", customerData.postalCode || "")}

          {select("Account status", "status", status, [
            ["1", "Active"],
            ["2", "Inactive"],
            ["3", "Blocked"],
          ])}

          {field("Registration date", "signupDate", registration, "date")}
        </div>

        {area("Full address", "address", customerData.fullAddress || "")}

        {area("Support notes", "note", customerData.supportNotes || "")}

        {detailRows([
          ["Linked order count (calculated)", customerData.orderCount ?? 0],
          [
            "Non-cancelled order value (calculated)",
            money(customerData.nonCancelledOrderValue ?? 0),
          ],
        ])}

        <p className={"hint"}>
          {
            "Customer status describes this contact record; it does not change admin authentication. Blank dates and contact fields remain blank. Order links and customer ID are preserved."
          }
        </p>

        {error && (
          <p className={"hint"} role={"alert"}>
            {esc(error)}
          </p>
        )}

        {success && (
          <p className={"hint"} role={"status"}>
            {esc(success)}
          </p>
        )}

        <div className={"dialog-actions"}>
          {btn("Cancel", `data-nav="customer" data-id="${customerId}"`, true)}

          <button className={"btn"} type={"submit"} disabled={saving}>
            {saving ? "Saving..." : "Save customer"}
          </button>
        </div>

        {errorSlot()}
      </form>
    </>
  );
}

export default function CustomerFormPage({ recordId } = {}) {
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    async function loadCustomer() {
      setLoading(true);
      setError("");
      setSuccess("");
      setCustomerData(null);

      if (recordId == null || recordId === "") {
        setError("Customer ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${CUSTOMERS_API}/${encodeURIComponent(recordId)}`,
          {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );

        if (response.status === 404) {
          setError("Customer not found.");
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to load customer (${response.status}).`);
        }

        const data = await response.json();
        setCustomerData(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading customer:", err);
          setError(err.message || "Unable to load customer.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadCustomer();

    return () => controller.abort();
  }, [recordId]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (!customerData || saving) return;

    setError("");
    setSuccess("");

    const formData = new FormData(event.currentTarget);

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const city = String(formData.get("city") || "").trim();
    const fullAddress = String(formData.get("address") || "").trim();
    const customerState = formData.get("state")?.toString().trim() || "";
    const postalCode = formData.get("postalCode")?.toString().trim() || "";
    const supportNotes = String(formData.get("note") || "").trim();
    const selectedStatus = Number(formData.get("status"));
    const dateValue = String(formData.get("signupDate") || "");

    if (!name || !email) {
      setError("Name and email are required.");
      return;
    }

    if (![1, 2, 3].includes(selectedStatus)) {
      setError("Please select a valid account status.");
      return;
    }

    const payload = {
      name,
      email,
      phone: phone || null,
      city: city || null,
      state: customerState || null,
      postalCode: postalCode || null,
      fullAddress: fullAddress || null,
      status: selectedStatus,
      registrationDate: dateValue || null,
      supportNotes: supportNotes || null,
    };

    setSaving(true);

    try {
      const response = await fetch(
        `${CUSTOMERS_API}/${encodeURIComponent(customerData.customerProfileId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        let message = `Update failed (${response.status}).`;

        try {
          const result = await response.json();
          message =
            result.message ||
            result.title ||
            (result.errors
              ? Object.values(result.errors).flat().join(" ")
              : message);
        } catch {
          // Keep the HTTP status message if the response is not JSON.
        }

        throw new Error(message);
      }

      // Refresh from the server so calculated values and saved fields
      // reflect the persisted database record.
      const refreshed = await fetch(
        `${CUSTOMERS_API}/${encodeURIComponent(customerData.customerProfileId)}`,
        {
          headers: { Accept: "application/json" },
        },
      );

      if (refreshed.ok) {
        setCustomerData(await refreshed.json());
      }

      setSuccess("Customer updated successfully.");

      // The server has saved the changes, so navigation should not
      // trigger the unsaved-changes confirmation.
      state.navigationApproved = true;

      const customersNav = document.querySelector('[data-nav="customers"]');

      if (customersNav) {
        customersNav.click();
      } else {
        // Allow navigation to be retried if the element is unavailable.
        state.navigationApproved = false;
        console.warn("Customers navigation element was not found.");
      }
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(err.message || "Unable to update customer.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className={"card"}>{"Loading customer details..."}</div>;
  }

  if (!customerData && error === "Customer not found.") {
    return notFound();
  }

  if (!customerData) {
    return (
      <div className={"card"} role={"alert"}>
        {esc(error || "Unable to load customer.")}
      </div>
    );
  }

  return tableViews(
    customerEdit(recordId, customerData, handleSubmit, saving, error, success),
  );
}
